import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Plus } from "lucide-react";
import { observer } from "mobx-react-lite";

import type { ListEventCategories } from "../../../api";
import type { AppModel } from "../../../app/app-model";

import { Button } from "../../../components/ui-kit/button";
import { CountBadge } from "../../../components/ui-kit/count-badge";
import { useShortcut } from "../../../utils/use-shortcut";
import { AddEventsDialog, hideShortcut } from "./add-events-dialog";

/**
 * The "Add events" button and the dialog it opens. The draft lives on the
 * patient, so it survives both hiding the dialog and switching to another tab
 * (which unmounts this whole subtree).
 */
export const AddEventsAction = observer(function AddEventsAction({
  app,
  eventOptions,
  onSaved,
}: {
  app: AppModel;
  eventOptions: ListEventCategories | null;
  onSaved: () => void;
}) {
  const { t } = useLingui();
  const draft = app.requirePatient().eventsDraft;
  // Only counted while hidden: with the rows on screen there is nothing waiting,
  // and the short-circuit keeps this subtree from re-rendering as the user types.
  const hiddenRowCount = draft.dialogOpen ? 0 : draft.startedRowCount();

  // Toggling the dialog, so peeking at the events already on record and coming
  // back never needs the mouse. Deliberately fires while typing — hiding
  // mid-sentence is the point — which is also why it captures: the rich-text
  // editors bind Mod-E to inline code, and consuming the event first keeps them
  // from acting on it too.
  useShortcut(hideShortcut.key, () => (draft.dialogOpen = !draft.dialogOpen), {
    capture: true,
    // Only act when our own dialog is the only one on screen. Captured
    // page-wide, an unscoped binding would steal Mod-E from the row-edit
    // dialog's editors, or hide this dialog out from under the discard prompt.
    // Closing dialogs linger for their exit animation, so don't count those.
    enabled: () =>
      document.querySelectorAll(
        "[data-slot='dialog-content']:not([data-state='closed'])",
      ).length === (draft.dialogOpen ? 1 : 0),
  });

  return (
    <>
      <Button
        variant="primary"
        icon={Plus}
        onClick={() => {
          draft.dialogOpen = true;
        }}
        aria-keyshortcuts={hideShortcut.aria}
        title={
          hiddenRowCount > 0
            ? `${plural(hiddenRowCount, {
                one: "Continue adding events — # unsaved row kept",
                other: "Continue adding events — # unsaved rows kept",
              })} (${hideShortcut.hint})`
            : `${t`Add events`} (${hideShortcut.hint})`
        }
      >
        <Trans>Add events</Trans>
        <CountBadge count={hiddenRowCount} />
      </Button>
      {draft.dialogOpen && (
        <AddEventsDialog
          app={app}
          eventOptions={eventOptions}
          draft={draft}
          onHide={() => (draft.dialogOpen = false)}
          onClose={() => draft.reset()}
          onSaved={onSaved}
        />
      )}
    </>
  );
});
